import { AnalyticsEventFragment } from 'services/graphql/fragments/HomeTilesContentFragment';
import { gql } from 'urql';

const VXLTextFragment = gql`
  fragment VXLTextFragment on VXLText {
    copy
    style {
      fontStyle
      color
    }
  }
`;

const IconStyleDictionaryFragment = gql`
  fragment IconStyleDictionaryFragment on IconStyleDictionary {
    __typename
    id
    iconKey
    fallbackURL
    tint
    size
  }
`;

const IllustrationStyleDictionaryFragment = gql`
  fragment IllustrationStyleDictionaryFragment on IllustrationStyleDictionary {
    __typename
    id
    illustrationKey
    fallbackURL
  }
`;

const TagFragment = gql`
  fragment TagFragment on Tag {
    id
    tagColor
    text
    icon
  }
`;

const FullWidthTextComponentFragment = gql`
  fragment FullWidthTextComponentFragment on FullWidthTextComponent {
    id
    maxLines
    textAlignment
    text {
      ...VXLTextFragment
    }
  }
`;

const SpacingComponentFragment = gql`
  fragment SpacingComponentFragment on SpacingComponent {
    id
    spacing
  }
`;

const ButtonComponentFragment = gql`
  fragment VXLButtonFragment on VXLButton {
    __typename
    copy {
      # we don't support custom text on the vxl buttons so only request copy value
      ...VXLTextFragment
    }
    horizontalLayout
    verticalLayout
    onDarkBackground
    style
    isEnabled
  }

  fragment ButtonComponentFragment on ButtonComponent {
    __typename
    id
    button {
      ...VXLButtonFragment
    }
    onClick {
      ...ButtonComponentActionResultFragment
    }
  }

  fragment ButtonComponentActionResultFragment on ButtonComponentActionResult {
    ...SDLaunchBelieveOnboardingNavigationFragment
    ...SDLaunchBelieveTransferFundsNavigationFragment
  }
`;

const DMGMScreenTilesErrorFragment = gql`
  fragment DMGMScreenTilesErrorFragment on DMGMScreenTilesError {
    code
    message
    title
  }
`;

const AdvanceTitledListTileFragment = gql`
  fragment FullWidthTextComponentTileFragment on FullWidthTextComponentTile {
    id
    status
    tileType

    text {
      ...VXLTextFragment
    }
    textAlignment
  }

  fragment InfoListTileFragment on InfoListTile {
    __typename
    id
    status
    tileType
    rowItems {
      id
      clickAnalytics {
        __typename
        ...AnalyticsEventFragment
      }
      description {
        ...VXLTextFragment
      }
      primaryTextV2 {
        ...VXLTextFragment
      }
      footnoteText {
        ...VXLTextFragment
      }
      image {
        ...IllustrationStyleDictionaryFragment
      }
      onClick {
        ...PresentBottomSheetActionFragment
      }
      rowInfoTitle {
        ...VXLTextFragment
      }
      secondaryText {
        ...VXLTextFragment
      }
    }
  }

  fragment HtmlComponentTileFragment on HtmlComponentTile {
    __typename
    id
    status
    tileType

    html {
      ...HtmlComponentFragment
    }
  }

  fragment SDInlineAlertFragment on SDInlineAlert {
    __typename
    icon {
      ...IconStyleDictionaryFragment
    }
    title {
      ...VXLTextFragment
    }
    body {
      ...VXLTextFragment
    }
    buttons {
      ...ButtonComponentFragment
    }
    backgroundColor
  }

  fragment SDInlineAlertTileFragment on SDInlineAlertTile {
    __typename
    id
    status
    tileType
    alert {
      ...SDInlineAlertFragment
    }
  }

  fragment AdvanceTitledListTileFragment on AdvanceTitledListTile {
    id
    layoutVersion
    status
    tileType
    vxlTitle {
      ...VXLTextFragment
    }
    tiles {
      id
      status
      tileType
      __typename
      ...FullWidthTextComponentTileFragment
      ...InfoListTileFragment
      ...HtmlComponentTileFragment
      ...SDInlineAlertTileFragment
    }
  }
`;

const VXLSeparatedVerticalStackContainerFragment = gql`
  fragment VXLSeparatedVerticalStackContainerFragment on VXLSeparatedVerticalStackContainer {
    id
    items {
      ...VXLSeparatedVerticalStackContainerChildFragment
    }
  }

  fragment VXLSeparatedVerticalStackContainerChildFragment on VXLSeparatedVerticalStackContainerChild {
    id
    content {
      __typename
      ...VXLSeparatedVerticalStackContainerChildResultFragment
    }
    onClick {
      __typename
      ...VXLSeparatedVerticalStackContainerChildActionFragment
    }
  }

  fragment VXLSeparatedVerticalStackContainerChildResultFragment on VXLSeparatedVerticalStackContainerChildResult {
    ...SDProductStatusRowComponentFragment
    ...ButtonComponentFragment
    ...FullWidthTextComponentFragment
    ...SpacingComponentFragment
    ...HtmlComponentFragment
  }

  fragment VXLSeparatedVerticalStackContainerChildActionFragment on VXLSeparatedVerticalStackContainerChildAction {
    ...SDLaunchVLOCNavigationFragment
    ...SDLaunchAdvanceOnboardingNavigationFragment
    ...SDLaunchAdvanceAccountNavigationFragment
    ...PresentBottomSheetActionFragment
  }
`;

const HtmlComponentFragment = gql`
  fragment HtmlComponentFragment on HtmlComponent {
    id
    html
    boldStyle {
      fontStyle
      color
    }
    linkStyle {
      fontStyle
      color
    }
    textStyle {
      fontStyle
      color
    }
  }
`;

const PresentBottomSheetActionFragment = gql`
  fragment PresentBottomSheetActionFragment on PresentBottomSheetAction {
    id
    elements {
      __typename
      ...ButtonComponentFragment
      ...FullWidthTextComponentFragment
      ...SpacingComponentFragment
      ...VXLBottomSheetSeparatedVerticalStackContainerFragment
      ...VXLHairlineComponentFragment
    }
    analyticsEvent {
      ...AnalyticsEventFragment
    }
  }

  fragment VXLBottomSheetSeparatedVerticalStackContainerFragment on VXLSeparatedVerticalStackContainer {
    id
    items {
      ...VXLBottomSheetSeparatedVerticalStackContainerChildFragment
    }
  }

  fragment VXLBottomSheetSeparatedVerticalStackContainerChildFragment on VXLSeparatedVerticalStackContainerChild {
    __typename
    id
    content {
      __typename
      ...VXLBottomSheetSeparatedVerticalStackContainerChildResultFragment
    }
    onClick {
      __typename
      ...VXLBottomSheetSeparatedVerticalStackContainerChildActionFragment
    }
  }

  fragment VXLBottomSheetSeparatedVerticalStackContainerChildResultFragment on VXLSeparatedVerticalStackContainerChildResult {
    ...SDProductStatusRowComponentFragment
    ...ButtonComponentFragment
    ...FullWidthTextComponentFragment
    ...SpacingComponentFragment
    ...HtmlComponentFragment
  }

  fragment VXLBottomSheetSeparatedVerticalStackContainerChildActionFragment on VXLSeparatedVerticalStackContainerChildAction {
    ...SDLaunchVLOCNavigationFragment
    ...SDLaunchAdvanceOnboardingNavigationFragment
    ...SDLaunchAdvanceAccountNavigationFragment
  }
`;

const SDLaunchVLOCNavigationFragment = gql`
  fragment SDLaunchVLOCNavigationFragment on SDLaunchVLOCNavigation {
    __typename
    id
    analytics {
      ...AnalyticsEventFragment
    }
  }
`;

const SDLaunchAdvanceOnboardingNavigationFragment = gql`
  fragment SDLaunchAdvanceOnboardingNavigationFragment on SDLaunchAdvanceOnboardingNavigation {
    __typename
    id
    analytics {
      ...AnalyticsEventFragment
    }
  }
`;

const SDLaunchAdvanceAccountNavigationFragment = gql`
  fragment SDLaunchAdvanceAccountNavigationFragment on SDLaunchAdvanceAccountNavigation {
    __typename
    id
    analytics {
      ...AnalyticsEventFragment
    }
  }
`;

const SDLaunchBelieveOnboardingNavigationFragment = gql`
  fragment SDLaunchBelieveOnboardingNavigationFragment on SDLaunchBelieveOnboardingNavigation {
    __typename
    id
    analytics {
      ...AnalyticsEventFragment
    }
  }
`;

const SDLaunchBelieveTransferFundsNavigationFragment = gql`
  fragment SDLaunchBelieveTransferFundsNavigationFragment on SDLaunchBelieveTransferFundsNavigation {
    __typename
    id
    analytics {
      ...AnalyticsEventFragment
    }
  }
`;

const LeadingTrailingTextAccessoriesRowComponentFragment = gql`
  fragment VXLTextStackAccessoryFragment on VXLTextStackAccessory {
    __typename
    id
    title {
      ...FullWidthTextComponentFragment
    }
    subtitle {
      ...FullWidthTextComponentFragment
    }
    icon {
      ...IconStyleDictionaryFragment
    }
    iconEdge
  }

  fragment LeadingTrailingTextAccessoriesRowComponentFragment on LeadingTrailingTextAccessoriesRowComponent {
    __typename
    id
    leading {
      ...VXLTextStackAccessoryFragment
    }
    trailing {
      ...VXLTextStackAccessoryFragment
    }
    leadingOnClick {
      ...VXLTextStackAccessoryActionResultFragment
    }
    trailingOnClick {
      ...VXLTextStackAccessoryActionResultFragment
    }
  }

  fragment VXLTextStackAccessoryActionResultFragment on VXLTextStackAccessoryActionResult {
    ...PresentBottomSheetActionFragment
  }
`;

const ProgressBarComponentFragment = gql`
  fragment VXLColorTypeFragment on VXLColorType {
    id
    color
  }

  fragment ProgressBarComponentFragment on ProgressBarComponent {
    __typename
    id
    percentage
    fillStyle {
      ...IllustrationStyleDictionaryFragment
      ...VXLColorTypeFragment
    }
  }
`;

const VXLPageSectionVerticalStackContainerFragment = gql`
  fragment VXLPageSectionVerticalStackContainerFragment on VXLPageSectionVerticalStackContainer {
    id
    content {
      __typename
      ...ProgressBarComponentFragment
      ...LeadingTrailingTextAccessoriesRowComponentFragment
      ...ButtonComponentFragment
      ...FullWidthTextComponentFragment
      ...SpacingComponentFragment
      ...VXLSeparatedVerticalStackContainerFragment
      ...HtmlComponentFragment
    }
  }
`;

const TileServiceTileFragment = gql`
  fragment TileServiceTileFragment on TileServiceTile {
    id
    status
    tileType
    container {
      __typename
      ...VXLPageSectionVerticalStackContainerFragment
    }
  }
`;

const GroupedContentSectionScreenTileFragment = gql`
  fragment GroupedContentSectionScreenTileFragment on GroupedContentSectionScreenTile {
    id
    status
    tileType
    tiles {
      __typename
      ...TileServiceTileFragment
      ...AdvanceTitledListTileFragment
    }
    displayAnalytics {
      ...AnalyticsEventFragment
    }
  }
`;

const SDProductStatusRowComponentFragment = gql`
  fragment SDProductStatusRowComponentFragment on SDProductStatusRowComponent {
    title {
      ...VXLTextFragment
    }
    icon {
      ...IconStyleDictionaryFragment
    }
    accessory {
      __typename
      ...SDProductStatusRowComponentAccessoryResultFragment
    }
  }

  fragment SDProductStatusRowComponentAccessoryResultFragment on SDProductStatusRowComponentAccessoryResult {
    ...TagFragment
    ...VXLTextFragment
  }
`;

const VXLHairlineComponentFragment = gql`
  fragment VXLHairlineComponentFragment on VXLHairlineComponent {
    id
    color
  }
`;

const FetchDMGMScreenTiles = gql`
  ${AnalyticsEventFragment}
  ${SpacingComponentFragment}
  ${IconStyleDictionaryFragment}
  ${IllustrationStyleDictionaryFragment}
  ${VXLTextFragment}
  ${FullWidthTextComponentFragment}
  ${ProgressBarComponentFragment}
  ${LeadingTrailingTextAccessoriesRowComponentFragment}
  ${VXLPageSectionVerticalStackContainerFragment}
  ${AdvanceTitledListTileFragment}
  ${TileServiceTileFragment}
  ${GroupedContentSectionScreenTileFragment}
  ${DMGMScreenTilesErrorFragment}
  ${VXLSeparatedVerticalStackContainerFragment}
  ${ButtonComponentFragment}
  ${HtmlComponentFragment}
  ${SDProductStatusRowComponentFragment}
  ${VXLHairlineComponentFragment}
  ${TagFragment}

  ${SDLaunchVLOCNavigationFragment}
  ${SDLaunchAdvanceOnboardingNavigationFragment}
  ${SDLaunchAdvanceAccountNavigationFragment}
  ${SDLaunchBelieveOnboardingNavigationFragment}
  ${SDLaunchBelieveTransferFundsNavigationFragment}
  ${PresentBottomSheetActionFragment}

  query fetchDMGMScreenTiles($input: DMGMScreenTilesInput!) {
    dMGMScreenTiles(input: $input) {
      __typename
      ...GroupedContentSectionScreenTileFragment
      ...DMGMScreenTilesErrorFragment
    }
  }
`;

export default FetchDMGMScreenTiles;
